import { useEffect, useState } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js'

import { HERO_OUTLINE } from '../../../../shared/constants/heroOutline.js'

import { ApartmentOutlinesIcon }  from './ApartmentOutlinesIcon.jsx'
import { BuildingOutlinesIcon }   from './BuildingOutlinesIcon.jsx'
import { ButtonHero }             from '../ButtonHero/ButtonHero.jsx'
import { TierOutlinesIcon }       from './TierOutlinesIcon.jsx'

import { ArrowLeft }     from '../../../../shared/ui/icons/ArrowLeft.jsx'
import { ChevronDown }   from '../../../../shared/ui/icons/ChevronDown.jsx'

import styles from './ModelControls.module.scss'


export function OutlineLayerSelector({
	activeOutline,
	availableOutlines = [],
	onOutlineChange,
	isMobile = false,
	showBackButton = false,
	onBack,
	useWideOutlineButtons = false,
	keepSelectorPinned = false,
	onDesktopExpandedChange,
}) {
  const { t } = useI18n()
	const getProductsCategoryString = useProductStrings()
  
  const [isLayerExpanded,  setIsLayerExpanded]  = useState(false)
  const [hoveredLayerIdx,  setHoveredLayerIdx]  = useState(null)
	const [isSelectorBackHovered, setIsSelectorBackHovered] = useState(false)
	const [isApartmentSelectorCollapsed, setIsApartmentSelectorCollapsed] = useState(false)
  
  function outlineLabel(outline) {
    switch (outline) {
      case HERO_OUTLINE.BUILDINGS:  return t('hero.select_building')
      case HERO_OUTLINE.TIERS:      return t('hero.select_floor')
			case HERO_OUTLINE.APARTMENTS: return getProductsCategoryString('select', 'default')
      default:                      return t('hero.select_layer')
    }
  }
  
  function LayerIconFor({ outline }) {
    if (outline === HERO_OUTLINE.BUILDINGS)  return <BuildingOutlinesIcon />
    if (outline === HERO_OUTLINE.TIERS)      return <TierOutlinesIcon />
    if (outline === HERO_OUTLINE.APARTMENTS) return <ApartmentOutlinesIcon />
    return null
  }

	const siteBackLabel = t('hero.select_site')
	const isDesktopLayerExpanded = keepSelectorPinned ? !isApartmentSelectorCollapsed : isLayerExpanded

	const displayedOutlineLabel = isSelectorBackHovered
		? siteBackLabel
		: hoveredLayerIdx !== null
    ? outlineLabel(availableOutlines[hoveredLayerIdx])
    : outlineLabel(activeOutline)

	useEffect(() => {
		setIsApartmentSelectorCollapsed(false)
		setHoveredLayerIdx(null)
		setIsSelectorBackHovered(false)
	}, [activeOutline, isMobile])

	useEffect(() => {
		if (isMobile) return undefined

		onDesktopExpandedChange?.(isDesktopLayerExpanded)
		return () => onDesktopExpandedChange?.(false)
	}, [isDesktopLayerExpanded, isMobile, onDesktopExpandedChange])

	if (isMobile) {
		return (
			<div
				className={styles.mobileGroup}
				onClick={() => setIsLayerExpanded((prev) => !prev)}
			>
				{isLayerExpanded && (
					<div
						className={[
							styles.buttonsGroupBottomBar,
							// styles.mobileOutlineButtonsGroup,
						].filter(Boolean).join(' ')}
					>
						{showBackButton && (
							<ButtonHero
								className={`${styles.heroButton} ${styles.mobileOutlineButton}`}
								onClick={(event) => {
									event.stopPropagation()
									onBack?.()
									setIsLayerExpanded(false)
								}}
								title={siteBackLabel}
							>
								<ArrowLeft className={styles.icon} />
							</ButtonHero>
						)}
						{availableOutlines.map((outline) => (
							<ButtonHero
								key={outline}
								stretch={useWideOutlineButtons}
								className={[
									styles.heroButton,
									styles.mobileOutlineButton,
									activeOutline === outline ? styles.heroButtonActive : '',
								].filter(Boolean).join(' ')}
								onClick={(event) => {
									event.stopPropagation()
									onOutlineChange?.(outline)
									setIsLayerExpanded(false)
								}}
								title={outlineLabel(outline)}
							>
								<LayerIconFor outline={outline} />
							</ButtonHero>
						))}
					</div>
				)}

				<div className={`${styles.mobileLabel} ${isLayerExpanded ? styles.mobileLabelActive : ''}`}>
					<span className={styles.labelText}>{outlineLabel(activeOutline)}</span>
					<ChevronDown
						className={styles.iconSmall}
						style={{
							marginLeft: '0.25rem',
							transform: isLayerExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
							transition: 'transform 0.3s ease',
						}}
					/>
				</div>
			</div>
		)
	}

  return (
		<div
			className={styles.outlineSelector}
			onMouseEnter={() => {
				if (keepSelectorPinned) {
					setIsApartmentSelectorCollapsed(false)
					return
				}
				setIsLayerExpanded(true)
			}}
			onMouseLeave={() => {
				if (keepSelectorPinned) return
				setIsLayerExpanded(false)
				setHoveredLayerIdx(null)
				setIsSelectorBackHovered(false)
			}}
		>
			{isDesktopLayerExpanded && (
				<div
					className={[
						styles.buttonsGroup,
						styles.outlineButtonsGroup,
					].filter(Boolean).join(' ')}
				>
					{showBackButton && (
						<ButtonHero
							className={`${styles.heroButton} ${styles.outlineSelectorButton}`}
							onClick={(event) => {
								event.stopPropagation()
								onBack?.()
							}}
							onMouseEnter={() => setIsSelectorBackHovered(true)}
							onMouseLeave={() => setIsSelectorBackHovered(false)}
							title={siteBackLabel}
						>
							<ArrowLeft className={styles.icon} />
						</ButtonHero>
					)}
					{availableOutlines.map((outline, idx) => (
						<ButtonHero
							key={outline}
							stretch={useWideOutlineButtons}
							className={[
								styles.heroButton,
								styles.outlineSelectorButton,
								activeOutline === outline ? styles.heroButtonActive : '',
							].filter(Boolean).join(' ')}
							onClick={(event) => {
								event.stopPropagation()
								onOutlineChange?.(outline)
							}}
							onMouseEnter={() => {
								setHoveredLayerIdx(idx)
								setIsSelectorBackHovered(false)
							}}
							onMouseLeave={() => setHoveredLayerIdx(null)}
							title={outlineLabel(outline)}
						>
							<LayerIconFor outline={outline} />
						</ButtonHero>
					))}
				</div>
			)}

			<div
				className={`${styles.labelElement} ${isDesktopLayerExpanded ? styles.layerLabelActive : ''}`}
				onClick={() => {
					if (!keepSelectorPinned) return
					setIsApartmentSelectorCollapsed((prev) => !prev)
					setHoveredLayerIdx(null)
					setIsSelectorBackHovered(false)
				}}
			>
				<span className={styles.labelText}>
					{displayedOutlineLabel}
				</span>
				<ChevronDown
					className={styles.iconSmall}
					style={{
						transform: isDesktopLayerExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
						transition: 'transform 0.3s ease',
					}}
				/>
			</div>
		</div>
  )
}